import { addPlacement, createAlbum, setCover } from "../lib/commands.ts";
import { createId } from "../lib/ids.ts";
import type { AlbumDocument, AssetRecord } from "../lib/types.ts";
import * as db from "./indexeddb.ts";
import { createAssetFromBlob } from "./assets.ts";

export const SEED_META_KEY = "library-seeded";

const DEMO_ALBUMS = [
  {
    title: "Summer Memories",
    photos: [
      { label: "Beach Day", src: "/seed/summer-beach.jpg" },
      { label: "Golden Hour", src: "/seed/summer-golden-hour.jpg" },
      { label: "Mountain View", src: "/seed/summer-mountain.jpg" },
      { label: "Poolside", src: "/seed/summer-pool.jpg" },
    ],
  },
  {
    title: "City Walk",
    photos: [
      { label: "Downtown", src: "/seed/city-downtown.jpg" },
      { label: "Cafe Stop", src: "/seed/city-cafe.jpg" },
      { label: "Night Lights", src: "/seed/city-night.jpg" },
    ],
  },
] as const;

async function loadSeedPhoto(src: string): Promise<Blob> {
  const response = await fetch(src);
  if (!response.ok) {
    throw new Error(`Failed to load seed photo ${src}`);
  }
  const buffer = await response.arrayBuffer();
  const headerType = response.headers.get("content-type")?.split(";")[0]?.trim();
  const mimeType =
    headerType && headerType.startsWith("image/")
      ? headerType
      : src.endsWith(".png")
        ? "image/png"
        : "image/jpeg";
  return new Blob([buffer], { type: mimeType });
}

export async function isLibrarySeeded(): Promise<boolean> {
  return Boolean(await db.getMeta(SEED_META_KEY));
}

export async function seedLibraryIfNeeded(): Promise<boolean> {
  if (await isLibrarySeeded()) return false;
  await seedLibrary();
  await db.putMeta(SEED_META_KEY, { seededAt: new Date().toISOString() });
  return true;
}

export async function seedLibrary(): Promise<void> {
  const albums: AlbumDocument[] = [];
  const assets: AssetRecord[] = [];

  for (const demo of DEMO_ALBUMS) {
    let album = createAlbum(demo.title);
    const pageId = album.pages[0]!.id;
    let coverAssetId: string | null = null;

    for (const [index, photo] of demo.photos.entries()) {
      const assetId = createId();
      const blob = await loadSeedPhoto(photo.src);
      const asset = await createAssetFromBlob(assetId, blob, blob.type || "image/jpeg", photo.label);
      assets.push(asset);

      const partial =
        index === 0
          ? { x: 0.08, y: 0.1, width: 0.84, height: 0.8 }
          : {
              x: 0.1 + (index % 2) * 0.42,
              y: 0.12 + Math.floor(index / 2) * 0.38,
              width: 0.36,
              height: 0.34,
            };
      album = addPlacement(album, pageId, asset.id, partial);
      if (!coverAssetId) coverAssetId = asset.id;
    }

    if (coverAssetId) {
      album = setCover(album, coverAssetId);
    }
    albums.push(album);
  }

  await db.putAlbumAndAssets(albums, assets);
}
